import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  validateTemplate,
  validateTemplateAsync,
} from 'components/AppService/ExamV3Settings/ExportTemplates/validation';
import _, { keyBy, set } from 'lodash';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  ExamV3ExportTemplate,
  ExamV3ExportTemplates,
} from 'types/examV3ExportTemplates.type';
import type { IDefaultReducerState } from 'types/state.interface';
import { errorNotification } from 'utils/notifications';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import type { z } from 'zod';
import api from '../services/api.service';

export type TExamV3ExportTemplatesState = IDefaultReducerState & {
  exportTemplates: ExamV3ExportTemplates[];
  selectedExportTemplate: Partial<ExamV3ExportTemplate> | null;
  originalExportTemplate: Partial<ExamV3ExportTemplate> | null;
  detailsLoading: boolean;
  errors: Record<string, z.ZodIssue> | null;
};

export const initialState: TExamV3ExportTemplatesState = {
  hasErrors: false,
  loading: false,
  detailsLoading: false,
  exportTemplates: [],
  selectedExportTemplate: undefined,
  originalExportTemplate: null,
  errors: null,
};

const slice = createSlice({
  name: 'examV3ExportTemplates',
  initialState,
  reducers: {
    fetchExportTemplatesRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchExportTemplatesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    fetchExportTemplatesSuccess: (
      state,
      { payload }: PayloadAction<ExamV3ExportTemplates[]>
    ) => {
      state.exportTemplates = payload;
      finishedLoadingSuccess(state);
    },
    fetchExportTemplateRequest: (state) => {
      state.detailsLoading = true;
    },
    fetchExportTemplateFailure: (state) => {
      state.detailsLoading = false;
    },
    fetchExportTemplateSuccess: (
      state,
      { payload }: PayloadAction<ExamV3ExportTemplate>
    ) => {
      const payloadWithListIds = templateFromNetwork(payload);
      state.selectedExportTemplate = payloadWithListIds;
      state.originalExportTemplate = payloadWithListIds;
      state.detailsLoading = false;
    },
    deleteExportTemplateFailure: (state) => {
      finishedLoadingFailure(state);
    },
    examV3SelectExportTemplate: (
      state,
      { payload }: PayloadAction<Partial<ExamV3ExportTemplate> | null>
    ) => {
      const payloadWithListIds = templateFromNetwork(payload);
      state.selectedExportTemplate = payloadWithListIds;
      state.originalExportTemplate = payloadWithListIds;
    },
    examV3ExportTemplateOnChange: (
      state,
      { payload }: PayloadAction<{ path: string; value: any }>
    ) => {
      if (!state.selectedExportTemplate) {
        state.selectedExportTemplate = {};
      }
      set(state.selectedExportTemplate, `${payload.path}`, payload.value);
    },
    examV3ExportTemplateOnArrayFieldChange: (
      state,
      {
        payload,
      }: PayloadAction<{
        path: string;
        value: any;
      }>
    ) => {
      if (!state.selectedExportTemplate) {
        state.selectedExportTemplate = {};
      }
      set(state.selectedExportTemplate, `${payload.path}`, payload.value);
    },
    examV3ExportTemplateReset: (state) => {
      state.selectedExportTemplate = state.originalExportTemplate;
      state.hasErrors = false;
      state.errors = null;
    },
    setEditExportTemplateErrors: (
      state,
      { payload }: PayloadAction<Record<string, z.ZodIssue> | null>
    ) => {
      if (payload === null) {
        state.hasErrors = false;
        state.errors = null;
      } else {
        state.hasErrors = true;
        state.errors = payload;
      }
    },
  },
});

export default slice.reducer;

export const {
  examV3ExportTemplateOnChange,
  examV3SelectExportTemplate,
  examV3ExportTemplateReset,
  examV3ExportTemplateOnArrayFieldChange,
  setEditExportTemplateErrors,
} = slice.actions;

export const examV3ExportTemplatesSelector = (state: TRootState) =>
  state.examV3ExportTemplates.exportTemplates;

export const examV3ExportTemplatesLoadingSelector = (
  state: TRootState
): boolean => state.examV3ExportTemplates.loading;

export const examV3ExportTemplateLoadingSelector = (state: TRootState) =>
  state.examV3ExportTemplates.detailsLoading;

export const examV3ExportTemplatesSelectedTemplateSelector = (
  state: TRootState
) => state.examV3ExportTemplates.selectedExportTemplate;
export const examV3ExportTemplatesOriginalSelectedTemplateSelector = (
  state: TRootState
) => state.examV3ExportTemplates.originalExportTemplate;

export const examV3ExportTemplatesErrorsSelector = (state: TRootState) =>
  state.examV3ExportTemplates.errors;

export const fetchExamV3ExportTemplates = createAppAsyncThunk(
  'examV3Settings/fetchExamV3ExportTemplates',
  async (_, { dispatch }) => {
    try {
      dispatch(slice.actions.fetchExportTemplatesRequest());
      const result = await api.get({
        endpoint: `/apps/TE_EXAM_V3/export-templates`,
      });
      dispatch(slice.actions.fetchExportTemplatesSuccess(result));
    } catch (e) {
      dispatch(slice.actions.fetchExportTemplatesFailure());
      console.error(e);
    }
  }
);

export const fetchExamV3ExportTemplate = createAppAsyncThunk(
  'examV3Settings/fetchExamV3ExportTemplate',
  async (id: string, { dispatch }) => {
    try {
      dispatch(slice.actions.fetchExportTemplateRequest());
      const result = await api.get({
        endpoint: `/apps/TE_EXAM_V3/export-templates/${id}`,
      });
      dispatch(slice.actions.fetchExportTemplateSuccess(result));
    } catch (e) {
      dispatch(slice.actions.fetchExportTemplateFailure());
    }
  }
);

export const deleteExamV3ExportTemplate = createAppAsyncThunk(
  'examV3Settings/deleteExamV3ExportTemplate',
  async (id: string, { dispatch, getState }) => {
    try {
      const selectedTemplate =
        getState().examV3ExportTemplates.selectedExportTemplate;
      await api.delete({
        endpoint: `/apps/TE_EXAM_V3/export-templates/${id}`,
      });
      if (selectedTemplate) {
        dispatch(slice.actions.examV3SelectExportTemplate(null));
      }
      dispatch(fetchExamV3ExportTemplates());
    } catch (e) {
      dispatch(slice.actions.deleteExportTemplateFailure());
    }
  }
);

export const saveExamV3ExportTemplate = createAppAsyncThunk(
  'examV3Settings/saveExamV3ExportTemplate',
  async (_, { getState, dispatch }) => {
    const {
      _id,
      id,
      createdBy,
      createdAt,
      updatedAt,
      updatedBy,
      // @ts-expect-error Not in typing
      __v,
      ...template
    } = getState().examV3ExportTemplates.selectedExportTemplate;

    const templateWithoutIds = templateToNetwork(template);

    const validationResult = validateTemplate(templateWithoutIds);

    if (!validationResult.success) {
      dispatch(
        examV3EditExportTemplateHandleValidationFailure({
          validationResult,
          sendNotification: true,
        })
      );
      return;
    } else {
      dispatch(slice.actions.setEditExportTemplateErrors(null));
    }

    try {
      if (_id || id) {
        const result = await api.patch({
          endpoint: `/apps/TE_EXAM_V3/export-templates/${_id ?? id}`,
          data: templateWithoutIds,
        });
        dispatch(examV3SelectExportTemplate(result));
      } else {
        const result = await api.post({
          endpoint: `/apps/TE_EXAM_V3/export-templates/`,
          data: templateWithoutIds,
        });
        dispatch(examV3SelectExportTemplate(result));
      }
      dispatch(fetchExamV3ExportTemplates());
    } catch (e) {}
  }
);

export const examV3EditExportTemplateValidate = createAppAsyncThunk(
  'examV3Settings/examV3EditExportTemplateValidate',
  async (sendNotification: boolean, { dispatch, getState }) => {
    const template = getState().examV3ExportTemplates.selectedExportTemplate;
    const validationResult = await validateTemplateAsync(template);

    if (!validationResult.success) {
      dispatch(
        examV3EditExportTemplateHandleValidationFailure({
          validationResult,
          sendNotification,
        })
      );
      return;
    } else {
      dispatch(slice.actions.setEditExportTemplateErrors(null));
    }
  }
);

export const examV3EditExportTemplateHandleValidationFailure =
  createAppAsyncThunk(
    'examV3Settings/examV3EditExportTemplateHandleValidationFailure',
    (
      {
        validationResult,
        sendNotification,
      }: { validationResult: any; sendNotification: boolean },
      { dispatch }
    ) => {
      const errorMap = keyBy(validationResult.error.issues, 'path');
      dispatch(slice.actions.setEditExportTemplateErrors(errorMap));
      if (sendNotification) {
        errorNotification(
          'Export template could not be validated',
          ['Please verify fields marked in red'].concat(
            validationResult.error.issues.map((error) => error.message)
          )
        );
      }
      return;
    }
  );

const templateToNetwork = (
  template: Partial<ExamV3ExportTemplate>
): Partial<ExamV3ExportTemplate> => {
  const copy = _.cloneDeep(template);

  sortToNetwork(copy?.seatingList?.studentList);
  sortToNetwork(copy?.seatingList?.examRange);

  copy?.mainHeader?.forEach((header) => {
    delete header._id;
    delete header.id;
  });

  copy?.seatingList?.studentList?.header?.forEach((header) => {
    delete header._id;
    delete header.id;
  });

  copy?.seatingList?.studentList?.columns?.forEach((column) => {
    delete column._id;
    delete column.id;
  });

  copy?.seatingList?.examRange?.columns?.forEach((column) => {
    delete column._id;
    delete column.id;
  });

  return copy;
};

const templateFromNetwork = (template: Partial<ExamV3ExportTemplate>) => {
  if (!template) {
    return null;
  }

  const copy = _.cloneDeep(template);

  if (!copy?.mainHeaderNoOfColumns) {
    _.set(copy, 'mainHeaderNoOfColumns', 2);
  }

  if (!copy?.seatingList?.studentList?.headerNoOfColumns) {
    _.set(copy, 'seatingList.studentList.headerNoOfColumns', 2);
  }

  copy?.mainHeader?.forEach((header) => {
    if (header._id) {
      header.id = header._id;
    }
  });

  copy?.seatingList?.studentList?.header?.forEach((header) => {
    if (header._id) {
      header.id = header._id;
    }
  });

  copy?.seatingList?.studentList?.columns?.forEach((column) => {
    if (column._id) {
      column.id = column._id;
    }
  });

  copy?.seatingList?.examRange?.columns?.forEach((column) => {
    if (column._id) {
      column.id = column._id;
    }
  });

  sortFromNetwork(copy?.seatingList?.studentList);
  sortFromNetwork(copy?.seatingList?.examRange);

  return copy;
};

const sortFromNetwork = (
  config: { sortBy: number | string; columns: { id?: string }[] } | undefined
) => {
  if (typeof config?.sortBy !== 'number') {
    return;
  }

  const id = config.columns[config.sortBy]?.id;
  config.sortBy = id;
};

const sortToNetwork = (
  config: { sortBy: number | string; columns: { id?: string }[] } | undefined
) => {
  if (typeof config?.sortBy !== 'string') {
    return;
  }

  const index = config.columns?.findIndex(
    (column) => column.id === config.sortBy
  );

  config.sortBy = index !== -1 ? index : undefined;
};
